import React, { useState, useEffect } from "react";
import SmallPie from "../../Components/GraphPlain3dComponent/GraphPlain3dComponent";
import SingleBigPie from "../../Components/GraphPlain3dComponent/Graph3DSelector";
import "../../Components/OneComponent.css";
import { GET } from "../../Api/HttpsMethods";
import EndPoints from "../../Api/EndPoints";

const CombinedTriangle = ({
  questionPaperId,
  setMessageBox,
  setIsMessageBoxVisible,
  setActiveImg,
  ErrorImg,
  activeSubjectId,
  onReviewHistoryFetched,
  userId
}) => {
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data variables for the 4 pie charts
  const [chaptersPieData, setChaptersPieData] = useState([]);
  const [difficultyPieData, setDifficultyPieData] = useState([]);
  const [topicPieData, setTopicPieData] = useState([]);
  const [questionTypePieData, setQuestionTypePieData] = useState([]);

  
  const [selectedChapter, setSelectedChapter] = useState(null);

  const [reviewHistory, setReviewHistory] = useState([]); // Add state for review history

  useEffect(() => {
    const fetchReviewDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const url = `${EndPoints.PaperDetailsReview}?ques_paper_id=${questionPaperId}&user_id=${userId}`;
        const response = await GET.withToken(url, {
          setMessageBox,
          setIsMessageBoxVisible,
          setActiveImg,
          ErrorImg,
        });

        // Check for proper response structure
        if (response?.data?.question_paper) {
          const questionPaperData = response.data.question_paper;
          setApiData(questionPaperData);
          processDataForCharts(questionPaperData);

          // Extract and pass review history to parent
          const reviewHistoryData = questionPaperData.review_history || [];
          setReviewHistory(reviewHistoryData);
          if (onReviewHistoryFetched) {
            onReviewHistoryFetched(reviewHistoryData);
          }
        } else if (response?.question_paper) {
          const questionPaperData = response.question_paper;
          setApiData(questionPaperData);
          processDataForCharts(questionPaperData);

          // Extract and pass review history to parent
          const reviewHistoryData = questionPaperData.review_history || [];
          setReviewHistory(reviewHistoryData);
          if (onReviewHistoryFetched) {
            onReviewHistoryFetched(reviewHistoryData);
          }
        } else {
          console.error("Response structure:", response);
          setError("Invalid response structure - question_paper not found");
        }
      } catch (err) {
        console.error("Error fetching review details:", err);
        setError(`Failed to fetch data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (questionPaperId) {
      fetchReviewDetails();
    }
  }, [questionPaperId, userId, activeSubjectId]);

  const handleChapterSelect = (selectedData) => {
    if (!selectedData) {
      setSelectedChapter(null);
      // Reset to overall view
      updateSmallCharts(apiData.difficulty_question_type_counts_nested);
      return;
    }

    const selectedChapterData =
      apiData.difficulty_question_type_counts_nested.flatMap((subject) =>
        subject.chapters.filter(
          (chapter) => chapter.chapter_name === selectedData.chapter_name
        )
      );

    if (selectedChapterData.length > 0) {
      setSelectedChapter(selectedChapterData[0]);
      updateSmallChartsForChapter(selectedChapterData);
    }
  };

  const updateSmallChartsForChapter = (chapterData) => {
    // 1. Topics pie chart
    const topicData = chapterData
      .flatMap((chapter) =>
        chapter.topics.map((topic) => ({
          topic_name: topic.topic_name,
          count: (topic.easy || 0) + (topic.medium || 0) + (topic.tough || 0),
        }))
      )
      .filter((topic) => topic.count > 0);

    // Aggregate duplicate topics
    const topicCounts = topicData.reduce((acc, topic) => {
      acc[topic.topic_name] = (acc[topic.topic_name] || 0) + topic.count;
      return acc;
    }, {});

    const finalTopicData = Object.entries(topicCounts)
      .map(([name, count]) => ({ topic_name: name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6); // Show top 6 topics

    setTopicPieData(finalTopicData);

    // 2. Difficulty pie chart
    const difficultyData = [
      {
        difficulty: "Easy",
        count: chapterData.reduce(
          (sum, chapter) => sum + (chapter.easy || 0),
          0
        ),
      },
      {
        difficulty: "Medium",
        count: chapterData.reduce(
          (sum, chapter) => sum + (chapter.medium || 0),
          0
        ),
      },
      {
        difficulty: "Tough",
        count: chapterData.reduce(
          (sum, chapter) => sum + (chapter.tough || 0),
          0
        ),
      },
    ].filter((item) => item.count > 0);

    setDifficultyPieData(difficultyData);

    // 3. Question type pie chart
    const questionTypeData = chapterData
      .map((chapter) => ({
        question_type: chapter.ques_type_name,
        count:
          (chapter.easy || 0) + (chapter.medium || 0) + (chapter.tough || 0),
      }))
      .filter((item) => item.count > 0);

    setQuestionTypePieData(questionTypeData);
  };

  const updateSmallCharts = (data) => {
    // Original logic for overall view
    // 1. DIFFICULTY PIE DATA
    let totalEasy = 0,
      totalMedium = 0,
      totalTough = 0;
    data.forEach((item) => {
      totalEasy += item.easy || 0;
      totalMedium += item.medium || 0;
      totalTough += item.tough || 0;
    });

    setDifficultyPieData(
      [
        { difficulty: "Easy", count: totalEasy },
        { difficulty: "Medium", count: totalMedium },
        { difficulty: "Tough", count: totalTough },
      ].filter((item) => item.count > 0)
    );

    // 2. TOPIC PIE DATA
    const topicCounts = {};
    data.forEach((item) => {
      item.chapters?.forEach((chapter) => {
        chapter.topics?.forEach((topic) => {
          const topicName = topic.topic_name;
          const count =
            (topic.easy || 0) + (topic.medium || 0) + (topic.tough || 0);
          topicCounts[topicName] = (topicCounts[topicName] || 0) + count;
        });
      });
    });

    setTopicPieData(
      Object.entries(topicCounts)
        .map(([name, count]) => ({ topic_name: name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6)
    );

    // 3. QUESTION TYPE PIE DATA
    const questionTypeCounts = {};
    data.forEach((item) => {
      const typeName = item.ques_type_name;
      const count = (item.easy || 0) + (item.medium || 0) + (item.tough || 0);
      questionTypeCounts[typeName] =
        (questionTypeCounts[typeName] || 0) + count;
    });

    setQuestionTypePieData(
      Object.entries(questionTypeCounts)
        .map(([type, count]) => ({ question_type: type, count }))
        .filter((item) => item.count > 0)
    );
  };

  const processDataForCharts = (data) => {
    console.log("Processing data for activeSubjectId:", activeSubjectId);
    console.log(
      "Raw difficulty_question_type_counts_nested:",
      data.difficulty_question_type_counts_nested
    );

    if (
      !data.difficulty_question_type_counts_nested ||
      !Array.isArray(data.difficulty_question_type_counts_nested)
    ) {
      console.warn("No difficulty_question_type_counts_nested data found");
      setChaptersPieData([]);
      setDifficultyPieData([]);
      setTopicPieData([]);
      setQuestionTypePieData([]);
      return;
    }

    let filteredData = data.difficulty_question_type_counts_nested;

    if (activeSubjectId) {
      filteredData = data.difficulty_question_type_counts_nested.filter(
        (item) => item.subject_id === activeSubjectId
      );
    }

    if (filteredData.length === 0) {
      console.warn("No data found for activeSubjectId:", activeSubjectId);
      setChaptersPieData([]);
      setDifficultyPieData([]);
      setTopicPieData([]);
      setQuestionTypePieData([]);
      return;
    }

    // Process chapters for big pie chart
    const chapterCounts = {};
    filteredData.forEach((item) => {
      item.chapters?.forEach((chapter) => {
        const chapterName = chapter.chapter_name;
        const count =
          (chapter.easy || 0) + (chapter.medium || 0) + (chapter.tough || 0);
        chapterCounts[chapterName] = (chapterCounts[chapterName] || 0) + count;
      });
    });

    const chaptersChartData = Object.entries(chapterCounts)
      .map(([name, count]) => ({
        chapter_name: name,
        total_questions: count,
      }))
      .filter((item) => item.total_questions > 0);

    setChaptersPieData(chaptersChartData);
    updateSmallCharts(filteredData);
  };

  if (loading) {
    return (
      <div className="main-flex-container-r">
        <div className="loading-container">Loading chart data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-flex-container-r">
        <div className="error-container">
          <p>Error: {error}</p>
          <p>Please check the console for more details.</p>
        </div>
      </div>
    );
  }

  // Get subject name for dynamic titles
  const currentSubjectName = activeSubjectId
    ? apiData?.difficulty_question_type_counts_nested?.find(
        (item) => item.subject_id === activeSubjectId
      )?.subject_name
    : "All Subjects";

  return (
    <div className="main-flex-container-r">
      {/* BIG PIE CHART - Chapters Distribution for Active Subject */}
      <div className="main-big-div-r">
        <SingleBigPie
          dataList={chaptersPieData}
          labelField="chapter_name"
          valueField="total_questions"
          width="800px"
          height="600px"
          onSliceSelect={handleChapterSelect} // Add this prop
        />
      </div>

      {/* THREE SMALL PIE CHARTS */}
      <div className="main-small-row-r">
        {/* Small Chart 1: Topics */}
        <div className="small-div-r" style={{ marginLeft: "55px" }}>
          <SmallPie
            dataList={topicPieData}
            labelField="topic_name"
            valueField="count"
            // chartTitle={`Topics ${
            //   selectedChapter ? `for ${selectedChapter.chapter_name}` : ""
            // }`}
            chartTitle={`Topics`}
            tooltipHeader="Topic Details"
            pieType="topics"
            difficultyData={difficultyPieData}
            questionTypeData={questionTypePieData}
          />
        </div>

        {/* Small Chart 2: Difficulty */}
        <div className="small-div-r">
          <SmallPie
            dataList={difficultyPieData}
            labelField="difficulty"
            valueField="count"
            chartTitle={`Difficulty Level`}
            // chartTitle={`Difficulty Level ${
            //   selectedChapter ? `for ${selectedChapter.chapter_name}` : ""
            // }`}

            tooltipHeader="Difficulty Details"
            pieType="difficulty"
            topicData={topicPieData}
            questionTypeData={questionTypePieData}
          />
        </div>

        {/* Small Chart 3: Question Type */}
        <div className="small-div-r">
          <SmallPie
            dataList={questionTypePieData}
            labelField="question_type"
            valueField="count"
            // chartTitle={`Question Type ${
            //   selectedChapter ? `for ${selectedChapter.chapter_name}` : ""
            // }`}
            chartTitle={`Question Type`}
            tooltipHeader="Question Type Details"
            pieType="questionType"
            topicData={topicPieData}
            difficultyData={difficultyPieData}
          />
        </div>
      </div>
    </div>
  );
};

export default CombinedTriangle;
